import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNode } from '../contexts/NodeContext';
import {
  getNodeGroups,
  getNodeCamerasPaginated,
  getGroupCameras,
  addCamerasToGroup,
  removeCameraFromGroup,
  createGroup,
  updateGroup,
  deleteGroup,
} from '../services/api.service';
import { GroupForm, CameraGroupBadges, useGroupFirstPaging, groupCameraCounts, cameraTagStyle, PageTitle } from '@trinetra/shared';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LiveStreamPlayer from '../components/LiveStreamPlayer';
import { useNodeStream } from '../contexts/NodeStreamContext';
import {
  MagnifyingGlassIcon,
  FolderIcon,
  FolderOpenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  VideoCameraIcon,
  CheckIcon,
  TagIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 9;
/** Cameras per request when loading the full list (the node caps `limit` at 1000). */
const LIST_CHUNK = 1000;

/** Build a nested tree from a flat parent_id list (returned as-is if already nested). */
function buildGroupTree(list: any[]): any[] {
  if (!Array.isArray(list) || list.length === 0) return [];
  if (list.some((g) => Array.isArray(g.children) && g.children.length > 0)) return list;
  const map: Record<string, any> = {};
  const roots: any[] = [];
  for (const item of list) map[item.id] = { ...item, children: [] };
  for (const item of list) {
    if (item.parent_id && map[item.parent_id]) map[item.parent_id].children.push(map[item.id]);
    else roots.push(map[item.id]);
  }
  return roots;
}

/** Collect ids of every group that has children (used to expand the tree by default). */
function collectParentIds(tree: any[], acc = new Set<string>()): Set<string> {
  tree.forEach((g) => {
    if (g.children?.length) { acc.add(g.id); collectParentIds(g.children, acc); }
  });
  return acc;
}

/**
 * A camera tag's label. Nodes send tag OBJECTS ({ id, name, color, category_… }, the camera tags
 * feature); a plain string is still accepted so an older node's payload can't crash the page.
 * Rendering the object itself was React error #31 and blanked the whole Groups page.
 */
function tagName(tag: any): string {
  return typeof tag === 'string' ? tag : (tag?.name || '');
}

export default function GroupCameraMapPage() {
  const { selectedNode } = useNode();
  const nodeId = selectedNode?.nodeId;

  const { hasPermission } = useAuth();
  const canAssign = hasPermission('groups.assign');
  const canCreateGroup = hasPermission('groups.create');
  const canEditGroup = hasPermission('groups.edit');
  const canDeleteGroup = hasPermission('groups.delete');

  const [groups, setGroups] = useState<any[]>([]);
  const [cameras, setCameras] = useState<any[]>([]);
  const [totalCameras, setTotalCameras] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selected group
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupCameraIds, setGroupCameraIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Filter state
  const [filterMapped, setFilterMapped] = useState<'all' | 'mapped' | 'unmapped'>('all');
  const [filterTag, setFilterTag] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  // Pending changes
  const [pendingAdd, setPendingAdd] = useState<Set<string>>(new Set());
  const [pendingRemove, setPendingRemove] = useState<Set<string>>(new Set());

  // Camera previews stream over the shared, session-level connection.
  const { subscribe, unsubscribe, streams: allStreams, states, manager } = useNodeStream();

  // The grid: the selected group's cameras first, then every other camera to add to it —
  // one sequence over the whole camera list, paged here (see useGroupFirstPaging).
  // Tag and location narrow the list BEFORE it is paged, so every page stays full.
  const paging = useGroupFirstPaging({
    page,
    pageSize: PAGE_SIZE,
    groupId: selectedGroupId,
    memberIds: groupCameraIds,
    pendingAdd,
    cameras,
    filterMapped,
    include: (c: any) =>
      (!filterTag || (c.tags || []).some((t: any) => tagName(t) === filterTag)) &&
      (!filterLocation || (c.location || '').toLowerCase() === filterLocation.toLowerCase()),
    includeKey: `${filterTag}|${filterLocation}`,
  });

  // Mobile group panel toggle
  const [mobileGroupOpen, setMobileGroupOpen] = useState(false);

  // Group CRUD (merged from the old Groups page)
  const [groupModal, setGroupModal] = useState<{ mode: 'create' | 'edit'; group?: any } | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<any | null>(null);

  /**
   * Switching site starts the mapping over.
   *
   * Groups and cameras are both per-site, so everything pointing at one has to go: a selected
   * group id the new site hasn't got fetches nothing and leaves the right-hand panel describing
   * a group that isn't there, and PENDING adds/removes are worse — they are uncommitted edits
   * naming the old site's cameras, sitting under a Save button.
   */
  const firstNodeRef = useRef(true);
  useEffect(() => {
    if (firstNodeRef.current) { firstNodeRef.current = false; return; }
    setSelectedGroupId(null);
    setGroupCameraIds(new Set());
    setPendingAdd(new Set());
    setPendingRemove(new Set());
    setGroupModal(null);
    setGroupToDelete(null);
    setPage(1);
    setSearch('');
    setDebouncedSearch('');
    setFilterMapped('all');
    setFilterTag('');
    setFilterLocation('');
    setError('');
    setSuccess('');
  }, [nodeId]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch groups — normalize to a nested tree so children render inside their parent
  const autoExpandedForRef = useRef<string | null>(null);
  const fetchGroups = useCallback(async () => {
    if (!nodeId) return;
    try {
      const data = await getNodeGroups(nodeId);
      const tree = buildGroupTree(Array.isArray(data) ? data : data?.data || []);
      setGroups(tree);
      // Expand all parents on first load (per node) so the hierarchy is visible
      if (autoExpandedForRef.current !== nodeId) {
        setExpandedGroups(collectParentIds(tree));
        autoExpandedForRef.current = nodeId;
      }
    } catch { } finally {
      setLoading(false);
    }
  }, [nodeId]);

  // Fetch the WHOLE camera list for the current search — the grid orders and pages it itself,
  // group first (useGroupFirstPaging). Only the visible page's cards mount a live player, so
  // the list costs one request per LIST_CHUNK cameras, not a stream per camera.
  // The unsearched camera list, kept for the group tree's counts: a searched list would make
  // every group look smaller than it is.
  const [countSource, setCountSource] = useState<any[] | null>(null);
  const listSeqRef = useRef(0);
  const fetchCameras = useCallback(async () => {
    if (!nodeId) return;
    const seq = ++listSeqRef.current;
    setCamerasLoading(true);
    try {
      const all: any[] = [];
      for (let p = 1; ; p += 1) {
        const resp = await getNodeCamerasPaginated(nodeId, { page: p, limit: LIST_CHUNK, search: debouncedSearch || undefined });
        const rawCams = resp?.data || resp?.cameras || resp;
        const rows = Array.isArray(rawCams) ? rawCams : [];
        all.push(...rows);
        const total = resp?.total || resp?.pagination?.total || all.length;
        if (rows.length < LIST_CHUNK || all.length >= total) break;
      }
      if (seq !== listSeqRef.current) return; // a newer search has already answered
      setCameras(all);
      setTotalCameras(all.length);
      if (!debouncedSearch) setCountSource(all);
    } catch (err: any) {
      if (seq === listSeqRef.current) setError(err.message || 'Failed to load cameras');
    } finally {
      if (seq === listSeqRef.current) setCamerasLoading(false);
    }
  }, [nodeId, debouncedSearch]);

  // Fetch group cameras when group selected
  const fetchGroupCameras = useCallback(async () => {
    if (!nodeId || !selectedGroupId) { setGroupCameraIds(new Set()); return; }
    try {
      const data = await getGroupCameras(nodeId, selectedGroupId);
      const cams = Array.isArray(data) ? data : data?.data || [];
      setGroupCameraIds(new Set(cams.map((c: any) => c.id || c.camera_id)));
    } catch {
      setGroupCameraIds(new Set());
    }
  }, [nodeId, selectedGroupId]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);
  useEffect(() => { fetchCameras(); }, [fetchCameras]);
  useEffect(() => { fetchGroupCameras(); }, [fetchGroupCameras]);

  // Reset pending when group changes — and go back to page 1, which is where the newly
  // selected group's cameras are listed.
  useEffect(() => {
    setPendingAdd(new Set());
    setPendingRemove(new Set());
    setPage(1);
  }, [selectedGroupId]);

  // A filter that changes how many pages there are can leave the operator past the end.
  useEffect(() => {
    if (page > paging.totalPages) setPage(paging.totalPages);
  }, [page, paging.totalPages]);

  // Camera previews over the shared connection. Reconcile subscriptions with the
  // currently visible page: subscribe cameras that appear, unsubscribe those that
  // leave (so the ULLFU stops pushing off-screen tracks). The shared WS+PC stays
  // warm across pagination/search/navigation — no per-change ICE/TURN rebuild.
  const subscribedIdsRef = useRef<Set<string>>(new Set());
  const nodeName = selectedNode?.name ?? '';

  // The cards actually drawn — a group-run page can hold cameras from any server page.
  const visibleIdsKey = paging.pageCameras.map((c: any) => c.id || c.camera_id).join(',');
  useEffect(() => {
    if (!nodeId) return;
    const visible = visibleIdsKey ? visibleIdsKey.split(',') : [];
    const visibleSet = new Set(visible);
    for (const id of subscribedIdsRef.current) {
      if (!visibleSet.has(id)) unsubscribe(nodeId, id);
    }
    for (const id of visible) {
      if (!subscribedIdsRef.current.has(id)) subscribe(nodeId, nodeName, id);
    }
    subscribedIdsRef.current = visibleSet;
  }, [nodeId, nodeName, visibleIdsKey, subscribe, unsubscribe]);

  // Release all of this page's subscriptions on unmount (shared connection stays).
  useEffect(() => {
    return () => {
      if (nodeId) for (const id of subscribedIdsRef.current) unsubscribe(nodeId, id);
      subscribedIdsRef.current = new Set();
    };
  }, [nodeId, unsubscribe]);

  // Per-camera recovery wiring for LiveStreamPlayer → shared manager.
  const handleCameraStall = useCallback((camId: string) => {
    if (nodeId) manager()?.handleCameraStall(nodeId, camId);
  }, [nodeId, manager]);

  const handleCameraPlaying = useCallback((camId: string) => {
    if (nodeId) manager()?.handleCameraPlaying(nodeId, camId);
  }, [nodeId, manager]);

  const handleCameraRetry = useCallback((camId: string) => {
    if (nodeId) manager()?.retryCamera(nodeId, camId);
  }, [nodeId, manager]);

  // Toggle camera mapping. Without a selected group there is nothing to add the camera TO, so
  // the click is refused here and the card below is rendered as unavailable rather than
  // looking clickable and silently doing nothing.
  const toggleCamera = (cameraId: string) => {
    if (!selectedGroupId || !canAssign) return; // can't toggle without a group or permission
    const isCurrentlyMapped = groupCameraIds.has(cameraId);
    const isPendingAdd = pendingAdd.has(cameraId);
    const isPendingRemove = pendingRemove.has(cameraId);

    if (isCurrentlyMapped && !isPendingRemove) {
      setPendingRemove(prev => new Set(prev).add(cameraId));
      setPendingAdd(prev => { const s = new Set(prev); s.delete(cameraId); return s; });
    } else if (isCurrentlyMapped && isPendingRemove) {
      setPendingRemove(prev => { const s = new Set(prev); s.delete(cameraId); return s; });
    } else if (!isCurrentlyMapped && !isPendingAdd) {
      setPendingAdd(prev => new Set(prev).add(cameraId));
      setPendingRemove(prev => { const s = new Set(prev); s.delete(cameraId); return s; });
    } else if (!isCurrentlyMapped && isPendingAdd) {
      setPendingAdd(prev => { const s = new Set(prev); s.delete(cameraId); return s; });
    }
  };

  const isCameraMapped = (cameraId: string) => {
    if (pendingRemove.has(cameraId)) return false;
    if (pendingAdd.has(cameraId)) return true;
    return groupCameraIds.has(cameraId);
  };

  const isDirty = pendingAdd.size > 0 || pendingRemove.size > 0;
  // Whether a camera card is interactive at all: a group to assign into, and the permission
  // to do it. Everything about the card's affordances follows this.
  const canPick = !!selectedGroupId && canAssign;

  // Save
  const handleSave = async () => {
    if (!nodeId || !selectedGroupId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (pendingAdd.size > 0) {
        await addCamerasToGroup(nodeId, selectedGroupId, Array.from(pendingAdd));
      }
      for (const camId of pendingRemove) {
        await removeCameraFromGroup(nodeId, selectedGroupId, camId);
      }
      setSuccess(`Saved! ${pendingAdd.size} added, ${pendingRemove.size} removed.`);
      setPendingAdd(new Set());
      setPendingRemove(new Set());
      await fetchGroupCameras();
      await fetchGroups();
      // The cards' membership chips come from each camera's own `groups` array, so the
      // camera list has to be re-read too — otherwise they keep showing the pre-save
      // membership until the page is reloaded.
      await fetchCameras();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setPendingAdd(new Set());
    setPendingRemove(new Set());
  };

  // ── Group CRUD ──
  // Flat list for the parent-group selector (tree → list)
  const flatGroups = useMemo(() => {
    const out: any[] = [];
    const walk = (list: any[]) => list.forEach((g) => { out.push(g); if (g.children?.length) walk(g.children); });
    walk(groups);
    return out;
  }, [groups]);

  // The tree as drawn: each group carries its camera count INCLUDING sub-groups (distinct
  // cameras — see groupCameraCounts). Until the camera list has loaded, the badges fall back to
  // the server's direct `camera_count`.
  const countedGroups = useMemo(() => {
    if (!countSource) return groups;
    const counts = groupCameraCounts(groups, countSource);
    const annotate = (list: any[]): any[] =>
      list.map((g) => ({
        ...g,
        total_count: counts.get(g.id)?.total ?? g.camera_count,
        direct_count: counts.get(g.id)?.direct ?? g.camera_count,
        children: g.children?.length ? annotate(g.children) : g.children,
      }));
    return annotate(groups);
  }, [groups, countSource]);

  // A group can't be its own parent, nor a child of its own subtree
  const collectSubtreeIds = (g: any, acc = new Set<string>()): Set<string> => {
    acc.add(g.id);
    (g.children || []).forEach((c: any) => collectSubtreeIds(c, acc));
    return acc;
  };
  const parentOptions = useMemo(() => {
    if (groupModal?.mode !== 'edit' || !groupModal.group) return flatGroups;
    const excluded = collectSubtreeIds(groupModal.group);
    return flatGroups.filter((g) => !excluded.has(g.id));
  }, [flatGroups, groupModal]);

  const flashSuccess = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };

  const handleGroupSubmit = async (data: any) => {
    if (!nodeId) return;
    setError('');
    try {
      if (groupModal?.mode === 'edit' && groupModal.group) {
        await updateGroup(nodeId, groupModal.group.id, data);
        flashSuccess('Group updated.');
      } else {
        await createGroup(nodeId, data);
        flashSuccess('Group created.');
      }
      setGroupModal(null);
      await fetchGroups();
      await fetchCameras(); // group names are carried on each camera's `groups` array
      // Make sure the (possibly new) parent is expanded so the child is visible
      if (data.parent_id) setExpandedGroups((prev) => new Set(prev).add(data.parent_id));
    } catch (err: any) {
      setError(err.message || 'Failed to save group');
    }
  };

  const handleGroupDelete = async () => {
    if (!nodeId || !groupToDelete) return;
    setError('');
    try {
      await deleteGroup(nodeId, groupToDelete.id);
      // Clear selection if the selected group was in the deleted subtree
      if (selectedGroupId && collectSubtreeIds(groupToDelete).has(selectedGroupId)) {
        setSelectedGroupId(null);
      }
      flashSuccess('Group deleted.');
      await fetchGroups();
      await fetchCameras(); // drop the deleted group from the cards' membership chips
    } catch (err: any) {
      setError(err.message || 'Failed to delete group');
    } finally {
      setGroupToDelete(null);
    }
  };

  const groupActions = {
    onEdit: canEditGroup ? (g: any) => setGroupModal({ mode: 'edit', group: g }) : undefined,
    onDelete: canDeleteGroup ? (g: any) => setGroupToDelete(g) : undefined,
  };

  // Filtered cameras
  // Filters (mapped / tag / location) are applied inside the paging, before it slices.
  const filteredCameras = paging.pageCameras;

  const totalPages = paging.totalPages;

  // Get unique tags
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    cameras.forEach(c => (c.tags || []).forEach((t: any) => { const n = tagName(t); if (n) tags.add(n); }));
    return Array.from(tags).sort();
  }, [cameras]);

  // Get unique locations
  const allLocations = useMemo(() => {
    const locs = new Set<string>();
    cameras.forEach(c => { if (c.location) locs.add(c.location); });
    return Array.from(locs).sort();
  }, [cameras]);

  // Find selected group
  function findGroup(groups: any[], id: string): any | null {
    for (const g of groups) {
      if (g.id === id) return g;
      if (g.children) { const found = findGroup(g.children, id); if (found) return found; }
    }
    return null;
  }
  const selectedGroup = selectedGroupId ? findGroup(groups, selectedGroupId) : null;

  if (!nodeId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <FolderIcon className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-[15px] font-medium">No site selected</p>
        <p className="mt-1 text-[13px]">Select a site from the title bar to manage group-camera mapping.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
      </div>
    );
  }

  // One camera card — drawn in both halves of a page (the group, then the cameras to add).
  const renderCameraCard = (camera: any) => {
    const camId = camera.id || camera.camera_id;
    const mapped = isCameraMapped(camId);
    const isPending = pendingAdd.has(camId) || pendingRemove.has(camId);
    const stream = (nodeId ? allStreams.get(`${nodeId}:${camId}`) : null) || null;
    // One ring, not two: a pending change and membership both want the halo, so
    // pick the more urgent instead of letting two ring colours collide.
    const ringCls = isPending
      ? 'ring-2 ring-amber-400/80'
      : mapped ? 'ring-2 ring-brand/40' : '';

    return (
      <div
        key={camId}
        onClick={canPick ? () => toggleCamera(camId) : undefined}
        aria-disabled={!canPick}
        title={!selectedGroupId ? 'Select a group first' : undefined}
        className={`relative group rounded-lg sm:rounded-xl border-2 overflow-hidden transition-all ${
          canPick ? 'cursor-pointer active:scale-[0.98]' : 'cursor-not-allowed'
        } ${mapped
          ? 'border-brand shadow-lg shadow-brand/10'
          : `border-border ${canPick ? 'hover:border-muted-foreground/50' : ''}`
          } ${ringCls}`}
      >
        {/* Live Preview */}
        <div className="aspect-video bg-black relative">
          <LiveStreamPlayer
            stream={stream}
            connectionState={(nodeId ? states.get(`${nodeId}:${camId}`) : undefined) || 'pending'}
            muted
            onStalled={() => handleCameraStall(camId)}
            onPlaying={() => handleCameraPlaying(camId)}
            onRetry={() => handleCameraRetry(camId)}
          />
          {mapped && (
            <div
              className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 z-20 flex max-w-[85%] items-center gap-1 rounded-full bg-brand pl-1 pr-2 py-0.5 shadow-lg ring-2 ring-white/70"
              title={selectedGroup ? `In ${selectedGroup.name}` : 'In this group'}
            >
              <CheckIcon className="h-3 sm:h-3.5 w-3 sm:w-3.5 shrink-0 text-white" />
              {/* The group's own name, not a generic "in group": the same card is
                  re-used as the selection moves between groups, so the badge has to
                  say WHICH group it is claiming membership of. */}
              <span className="truncate text-[9px] sm:text-[10px] font-semibold text-white">
                {selectedGroup?.name || 'In group'}
              </span>
            </div>
          )}
          {isPending && (
            <div className="absolute top-1.5 sm:top-2 left-1.5 sm:left-2 z-20 bg-amber-500 rounded-full px-1.5 sm:px-2 py-0.5 text-[8px] sm:text-[9px] text-white font-semibold shadow">
              {pendingAdd.has(camId) ? '+ ADD' : '− REMOVE'}
            </div>
          )}
        </div>

        {/* Info */}
        <div className={`px-2 sm:px-3 py-1.5 sm:py-2 ${mapped ? 'bg-brand/10' : ''}`}>
          <h4 className={`text-[11px] sm:text-[12px] font-semibold truncate ${mapped ? 'text-brand' : 'text-foreground'}`}>{camera.name}</h4>
          {camera.location && <p className="text-[9px] sm:text-[10px] text-muted-foreground truncate">{camera.location}</p>}
          {/* Every group this camera is filed under — not just the selected one.
              Without it a camera already placed in three other groups looked the
              same as an unassigned one, so cameras got re-filed needlessly. */}
          <CameraGroupBadges
            groups={camera.groups}
            selectedGroupId={selectedGroupId}
            selectedGroupName={selectedGroup?.name}
            pendingAdd={pendingAdd.has(camId)}
            pendingRemove={pendingRemove.has(camId)}
            max={3}
          />
          {camera.tags && camera.tags.length > 0 && (
            <div className="hidden sm:flex gap-1 mt-1 flex-wrap">
              {camera.tags.slice(0, 3).map((tag: any) => (
                <span
                  key={tag?.id || tagName(tag)}
                  title={tag?.category_name ? `${tag.category_name}: ${tagName(tag)}` : tagName(tag)}
                  className="inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[9px]"
                  style={cameraTagStyle(tag?.color)}
                >
                  <TagIcon className="h-2.5 w-2.5" />{tagName(tag)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <PageTitle title="Camera Groups" subtitle="Create groups and assign cameras with live preview">
        {isDirty && (
          <button onClick={handleDiscard} className="h-8 rounded-lg border border-border px-3 text-xs font-medium text-muted-foreground hover:bg-muted transition-colors">
            Discard
          </button>
        )}
        {canAssign && (
          <button onClick={handleSave} disabled={!isDirty || saving || !selectedGroupId || !canAssign}
            className="h-8 rounded-lg bg-brand px-3 text-xs font-semibold text-white hover:bg-brand/90 disabled:opacity-50 transition-colors">
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </PageTitle>

      {error && <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 p-2.5 sm:p-3 text-[12px] sm:text-[13px] text-destructive">{error}</div>}
      {success && <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 p-2.5 sm:p-3 text-[12px] sm:text-[13px] text-green-700 dark:text-green-400">{success}</div>}

      {/* Mobile: Group selector bar */}
      <div className="lg:hidden">
        <button
          onClick={() => setMobileGroupOpen(!mobileGroupOpen)}
          className="w-full flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 shadow-card"
        >
          <div className="flex items-center gap-2 min-w-0">
            <FolderIcon className="h-4 w-4 text-yellow-400 shrink-0" />
            <span className="text-[13px] font-medium text-foreground truncate" title={selectedGroup?.name}>
              {selectedGroup ? selectedGroup.name : 'Select a group'}
            </span>
            {selectedGroup && (
              <span className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full shrink-0">
                {groupCameraIds.size}
              </span>
            )}
          </div>
          <ChevronDownIcon className={`h-4 w-4 text-muted-foreground transition-transform ${mobileGroupOpen ? 'rotate-180' : ''}`} />
        </button>
        {mobileGroupOpen && (
          <div className="mt-1 rounded-xl border border-border bg-card shadow-card max-h-60 overflow-y-auto p-2">
            {canCreateGroup && (
              <button
                onClick={() => { setGroupModal({ mode: 'create' }); setMobileGroupOpen(false); }}
                className="w-full mb-1 flex items-center justify-center gap-1.5 rounded-lg bg-brand/10 px-3 py-2 text-[12px] font-medium text-brand hover:bg-brand/20 transition-colors"
              >
                <PlusIcon className="h-4 w-4" /> New Group
              </button>
            )}
            {groups.length > 0 ? (
              renderGroupTree(countedGroups, selectedGroupId, (id: string) => { setSelectedGroupId(id); setMobileGroupOpen(false); }, expandedGroups, setExpandedGroups, groupActions)
            ) : (
              <p className="py-4 text-center text-[13px] text-muted-foreground">No groups found</p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 h-[calc(100vh-8.5rem)] sm:h-[calc(100vh-9rem)] lg:h-[calc(100vh-5rem)]">
        {/* Left: Group Tree — hidden on mobile */}
        <div className="hidden lg:flex w-72 flex-shrink-0 rounded-xl border border-border bg-card shadow-card flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-foreground">Select Group</h3>
            {canCreateGroup && (
              <button
                onClick={() => setGroupModal({ mode: 'create' })}
                title="Create group"
                className="flex h-7 items-center gap-1 rounded-lg bg-brand/10 px-2 text-[11px] font-medium text-brand hover:bg-brand/20 transition-colors"
              >
                <PlusIcon className="h-3.5 w-3.5" /> New
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {groups.length > 0 ? (
              renderGroupTree(countedGroups, selectedGroupId, setSelectedGroupId, expandedGroups, setExpandedGroups, groupActions)
            ) : (
              <div className="py-8 text-center">
                <p className="text-[13px] text-muted-foreground">No groups found</p>
                {canCreateGroup && (
                  <button onClick={() => setGroupModal({ mode: 'create' })} className="mt-2 text-[12px] font-medium text-brand hover:underline">
                    Create your first group
                  </button>
                )}
              </div>
            )}
          </div>
          {selectedGroup && (
            <div className="px-4 py-3 border-t border-border flex items-center justify-between gap-2">
              <p className="text-[11px] text-muted-foreground min-w-0 truncate" title={selectedGroup.name}>
                <span className="font-medium text-foreground">{selectedGroup.name}</span> — {groupCameraIds.size} camera{groupCameraIds.size !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-0.5 shrink-0">
                {canEditGroup && (
                  <button
                    onClick={() => setGroupModal({ mode: 'edit', group: selectedGroup })}
                    title="Edit group"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                )}
                {canDeleteGroup && (
                  <button
                    onClick={() => setGroupToDelete(selectedGroup)}
                    title="Delete group"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: Camera Grid */}
        <div className="flex-1 rounded-xl border border-border bg-card shadow-card flex flex-col overflow-hidden min-w-0">
          {/* Toolbar */}
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-border flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[120px] max-w-xs">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-1.5 sm:py-2 text-[12px] sm:text-[13px] text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              />
            </div>
            {selectedGroupId && (
              <select
                value={filterMapped}
                onChange={(e) => setFilterMapped(e.target.value as any)}
                className="rounded-lg border border-border bg-background px-2 sm:px-3 py-1.5 sm:py-2 text-[11px] sm:text-[13px] text-foreground"
              >
                <option value="all">All</option>
                <option value="mapped">Mapped</option>
                <option value="unmapped">Unmapped</option>
              </select>
            )}
            {allLocations.length > 0 && (
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="hidden sm:block rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground"
              >
                <option value="">All Locations</option>
                {allLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            )}
            {allTags.length > 0 && (
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="hidden sm:block rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground"
              >
                <option value="">All Tags</option>
                {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
              </select>
            )}
            {isDirty && (
              <span className="text-[10px] sm:text-[12px] text-amber-600 dark:text-amber-400 font-medium ml-auto whitespace-nowrap">
                +{pendingAdd.size} / −{pendingRemove.size}
              </span>
            )}
          </div>

          {/* Why the cards below don't respond — stated once, where the clicking happens. */}
          {!selectedGroupId && (
            <div className="flex items-start gap-2 border-b border-border bg-brand/5 px-3 sm:px-4 py-2 text-[11px] sm:text-[12px] text-muted-foreground">
              <FolderIcon className="h-4 w-4 shrink-0 text-brand" />
              <span>
                <span className="font-medium text-foreground">Select a group</span> to start assigning cameras —
                these previews are read-only until one is chosen.
              </span>
            </div>
          )}

          {/* Camera Grid */}
          <div className="flex-1 overflow-y-auto p-2.5 sm:p-4">
            {/* Which part of the list this page shows. The group's own cameras come first, so
                what the group holds is visible the moment it is selected; the rest follow,
                ready to be added. */}
            {selectedGroup && (
              <div className="mb-2.5 sm:mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-[12px]">
                {paging.groupOnPage > 0 ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-brand" />
                    <span className="font-semibold text-foreground">In {selectedGroup.name}</span>
                    <span className="text-muted-foreground">
                      {paging.groupCount} camera{paging.groupCount === 1 ? '' : 's'} · listed first
                      {paging.groupLastPage > 1 ? ` (pages 1–${paging.groupLastPage})` : ''}
                    </span>
                  </>
                ) : (
                  <>
                    <VideoCameraIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-foreground">Other cameras</span>
                    <span className="text-muted-foreground">
                      click to add to {selectedGroup.name}
                      {paging.groupLastPage > 0
                        ? ` · its ${paging.groupCount} camera${paging.groupCount === 1 ? ' is' : 's are'} on page${paging.groupLastPage > 1 ? `s 1–${paging.groupLastPage}` : ' 1'}`
                        : ''}
                    </span>
                  </>
                )}
                {/* Ticked on another page: say so here, where the operator is now looking. */}
                {pendingAdd.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setPage(1)}
                    className="ml-auto rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold text-amber-700 dark:text-amber-400 hover:bg-amber-500/25"
                    title="Cameras ticked to add are listed with the group, from page 1"
                  >
                    +{pendingAdd.size} selected to add{paging.groupOnPage === 0 ? ' — view on page 1' : ''}
                  </button>
                )}
              </div>
            )}
            {camerasLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
              </div>
            ) : filteredCameras.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <VideoCameraIcon className="h-10 sm:h-12 w-10 sm:w-12 mb-3 sm:mb-4 opacity-40" />
                <p className="text-[12px] sm:text-[13px]">
                  {!selectedGroupId
                    ? 'Select a group first'
                    : filterMapped === 'mapped'
                      ? `No cameras in ${selectedGroup?.name || 'this group'} yet`
                      : 'No cameras found'}
                </p>
              </div>
            ) : (
              <>
                {paging.groupOnPage > 0 && (
                  <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-4">
                    {filteredCameras.slice(0, paging.groupOnPage).map(renderCameraCard)}
                  </div>
                )}
                {filteredCameras.length > paging.groupOnPage && (
                  <>
                    {/* Where the group ends and the candidates begin, on the page they share. */}
                    {paging.groupOnPage > 0 && selectedGroup && (
                      <div className="my-3 sm:my-4 flex items-center gap-2 text-[11px] sm:text-[12px]">
                        <span className="h-px flex-1 bg-border" />
                        <span className="font-semibold text-foreground">Other cameras</span>
                        <span className="text-muted-foreground">— click to add to {selectedGroup.name}</span>
                        <span className="h-px flex-1 bg-border" />
                      </div>
                    )}
                    <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-4">
                      {filteredCameras.slice(paging.groupOnPage).map(renderCameraCard)}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-3 sm:px-4 py-2 sm:py-3 border-t border-border flex items-center justify-between">
              <span className="text-[10px] sm:text-[12px] text-muted-foreground">
                <span className="hidden sm:inline">Page </span>{page}/{totalPages}
                <span className="hidden sm:inline"> ({totalCameras} cameras)</span>
              </span>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-lg border border-border px-2 sm:px-3 py-1 sm:py-1.5 text-[12px] font-medium text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="sm:hidden text-[12px] font-medium text-foreground px-2">{page}</span>
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = page - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors ${page === pageNum ? 'bg-brand text-white' : 'text-foreground hover:bg-muted'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-lg border border-border px-2 sm:px-3 py-1 sm:py-1.5 text-[12px] font-medium text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dirty state bottom bar */}
      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between shadow-lg">
          <span className="text-[11px] sm:text-[13px] text-amber-600 dark:text-amber-400 font-medium truncate mr-2">
            ⚠ <span className="hidden sm:inline">Unsaved changes — </span>{pendingAdd.size} add, {pendingRemove.size} remove
          </span>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button onClick={handleDiscard} className="rounded-lg border border-border px-3 sm:px-4 py-1.5 text-[11px] sm:text-[12px] font-medium text-muted-foreground hover:bg-muted transition-colors">
              Discard
            </button>

            {canAssign && (
              <button onClick={handleSave} disabled={saving || !canAssign} className="rounded-lg bg-brand px-3 sm:px-4 py-1.5 text-[11px] sm:text-[12px] font-semibold text-white hover:bg-brand/90 disabled:opacity-50 transition-colors">
                {saving ? 'Saving…' : 'Save'}
              </button>

            )}
          </div>
        </div>
      )}

      {/* ── Create / Edit Group Modal ── */}
      {groupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setGroupModal(null)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">{groupModal.mode === 'edit' ? 'Edit Group' : 'Create Group'}</h2>
              <button
                onClick={() => setGroupModal(null)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <GroupForm
              group={groupModal.mode === 'edit' ? groupModal.group : null}
              parentGroups={parentOptions}
              onSubmit={handleGroupSubmit}
              onCancel={() => setGroupModal(null)}
            />
          </div>
        </div>
      )}

      {/* ── Delete Group Confirm ── */}
      <ConfirmDialog
        open={!!groupToDelete}
        title="Delete Group"
        message={`Are you sure you want to delete "${groupToDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleGroupDelete}
        onCancel={() => setGroupToDelete(null)}
      />
    </div>
  );
}

/* ─── Group Tree Renderer ─── */
interface GroupTreeActions {
  onEdit?: (g: any) => void;
  onDelete?: (g: any) => void;
}

function renderGroupTree(
  groups: any[],
  selectedId: string | null,
  onSelect: (id: string) => void,
  expanded: Set<string>,
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>,
  actions?: GroupTreeActions,
  level = 0,
) {
  const hasActions = !!(actions?.onEdit || actions?.onDelete);
  return groups.map((g: any) => {
    const hasChildren = g.children?.length > 0;
    const isExpanded = expanded.has(g.id);
    const isSelected = g.id === selectedId;
    return (
      <div key={g.id}>
        <div
          className={`group flex items-center gap-2 rounded-r-lg pr-2 py-1.5 cursor-pointer transition-colors ${isSelected ? 'font-semibold' : 'text-foreground hover:bg-muted'
            }`}
          style={{
            // The same treatment the sidebar gives its active entry — brand tint plus a 3px
            // brand edge — so "which one am I on" reads the same in both lists. Unselected
            // rows keep a transparent stripe so nothing shifts when the selection moves.
            paddingLeft: `${level * 16 + 8}px`,
            backgroundColor: isSelected ? 'var(--sidebar-active-bg)' : undefined,
            borderLeft: `3px solid ${isSelected ? 'var(--sidebar-active-border)' : 'transparent'}`,
            color: isSelected ? 'var(--brand)' : undefined,
          }}
          aria-current={isSelected ? 'true' : undefined}
          onClick={() => onSelect(g.id)}
        >
          <span
            className="w-4 h-4 flex items-center justify-center flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(prev => {
                const next = new Set(prev);
                next.has(g.id) ? next.delete(g.id) : next.add(g.id);
                return next;
              });
            }}
          >
            {hasChildren ? (isExpanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />) : null}
          </span>
          {isExpanded ? <FolderOpenIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" /> : <FolderIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />}
          <span className="text-[13px] font-medium truncate flex-1" title={g.name}>{g.name}</span>
          {hasActions && (
            <span
              className="flex items-center gap-0.5 shrink-0 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              {actions?.onEdit && (
                <button
                  onClick={() => actions.onEdit!(g)}
                  title="Edit group"
                  className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors"
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                </button>
              )}
              {actions?.onDelete && (
                <button
                  onClick={() => actions.onDelete!(g)}
                  title="Delete group"
                  className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          )}
          {/* A parent's badge counts its sub-groups too — a site whose cameras all sit in
              sub-groups used to read 0. The tooltip gives the split when it differs. */}
          {(g.total_count ?? g.camera_count) != null && (
            <span
              title={
                g.total_count != null && g.total_count !== g.direct_count
                  ? `${g.direct_count} directly in ${g.name} · ${g.total_count} including sub-groups`
                  : undefined
              }
              className={`text-[11px] px-1.5 py-0.5 rounded-full ${isSelected ? 'bg-brand/15 text-brand font-semibold' : 'text-muted-foreground bg-muted'}`}
            >
              {g.total_count ?? g.camera_count}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && renderGroupTree(g.children, selectedId, onSelect, expanded, setExpanded, actions, level + 1)}
      </div>
    );
  });
}
